import { useEffect, useState } from 'react'
import { useConnectorConversation } from '../../../../api/hooks/connectorConversation/useConnectorConversation'
import { ScoopSpinner, ScoopDialog, ScoopButton } from '../../../ui'
import { PresetSelection } from './components/PresetSelection/PresetSelection'
import { CustomIntent } from './components/CustomIntent/CustomIntent'
import { RefinementChat } from './components/RefinementChat/RefinementChat'
import { ModifyInput } from './components/ModifyInput/ModifyInput'
import styles from './ConnectorAutoConfig.module.scss'

// Internal UI steps (separate from backend phases)
const STEPS = {
    PRESET_SELECTION: 'preset_selection',
    CUSTOM_INTENT: 'custom_intent',
    REFINEMENT: 'refinement',
    MODIFY_INPUT: 'modify_input',
}

/**
 * AI-guided connector configuration flow
 */
export const ConnectorAutoConfig = ({
    open,
    connectorType,
    connectorName,
    connectionKey,
    workspaceID,
    onComplete,
    onCancel,
    onClose,
    onFallbackToManual,
    requireDateRange,
    objectList = [],
    objectListLoading = false,
    objectSelectionLabel = 'Property',
}) => {
    // API state
    const [sessionId, setSessionId] = useState(null)
    const [conversation, setConversation] = useState(null)
    const [error, setError] = useState(null)

    // UI state
    const [currentStep, setCurrentStep] = useState(STEPS.PRESET_SELECTION)
    const [stepHistory, setStepHistory] = useState([])
    const [selectedPreset, setSelectedPreset] = useState(null)
    const [customText, setCustomText] = useState('')
    const [modifyText, setModifyText] = useState('')
    const [datasetName, setDatasetName] = useState('')
    const [startDate, setStartDate] = useState(
        requireDateRange ? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) : null
    )
    // For connectors like GA4 the real "object" is the Property the user must
    // pick. The AI presets only carry a generic placeholder, so when the
    // connector exposes a real object list the user picks the one to extract
    // from. selectedObjectName holds the `name` of the chosen object.
    const requireObjectSelection = objectList.length > 0
    const [selectedObjectName, setSelectedObjectName] = useState(null)

    // Auto-select the object when the connector exposes exactly one, so the
    // user is never asked something that has no alternative.
    useEffect(() => {
        if (objectList.length === 1 && !selectedObjectName) {
            setSelectedObjectName(objectList[0].name)
        }
    }, [objectList, selectedObjectName])

    const {
        startConversation,
        startingConversation,
        selectPreset,
        selectingPreset,
        modifyConfig,
        modifyingConfig,
        accept,
        accepting,
        cancel,
    } = useConnectorConversation()

    // Start conversation on mount
    useEffect(() => {
        startConversation({
            connectorType,
            connectionKey,
            workspaceID,
            onSuccess: (response) => {
                setSessionId(response.sessionId)
                setConversation(response)
            },
            onError: (err) => {
                setError(err?.message || 'Failed to start conversation')
            },
        })
    }, [connectorType, connectionKey, workspaceID])

    // Navigate to a new step
    const goToStep = (step) => {
        setStepHistory((prev) => [...prev, currentStep])
        setCurrentStep(step)
    }

    // Go back to previous step
    const goBack = () => {
        if (stepHistory.length > 0) {
            const previousStep = stepHistory[stepHistory.length - 1]
            setStepHistory((prev) => prev.slice(0, -1))
            setCurrentStep(previousStep)
        } else {
            // No history, go back to setup choice
            handleCancel()
        }
    }

    // Handle preset selection
    const handleSelectPreset = (presetId) => {
        selectPreset({
            sessionId,
            presetId,
            onSuccess: (response) => {
                setConversation(response)
                if (response.objectLabel) {
                    setDatasetName(`${connectorName || connectorType} ${response.objectLabel}`)
                }
                if (response.dateRange?.start) {
                    setStartDate(new Date(response.dateRange.start))
                }
                goToStep(STEPS.REFINEMENT)
            },
            onError: (err) => {
                setError(err?.message || 'Failed to select preset')
            },
        })
    }

    // Handle custom intent submission
    const handleCustomIntentSubmit = () => {
        if (!customText.trim()) return
        selectPreset({
            sessionId,
            presetId: 'something_else',
            customIntent: customText.trim(),
            onSuccess: (response) => {
                setConversation(response)
                if (response.objectLabel) {
                    setDatasetName(`${connectorName || connectorType} ${response.objectLabel}`)
                }
                if (response.dateRange?.start) {
                    setStartDate(new Date(response.dateRange.start))
                }
                goToStep(STEPS.REFINEMENT)
            },
            onError: (err) => {
                setError(err?.message || 'Failed to process custom intent')
            },
        })
    }

    // Handle modification submission
    const handleModifySubmit = () => {
        if (!modifyText.trim()) return
        modifyConfig({
            sessionId,
            modification: modifyText.trim(),
            onSuccess: (response) => {
                setConversation(response)
                setModifyText('')
                if (response.dateRange?.start) {
                    setStartDate(new Date(response.dateRange.start))
                }
                goToStep(STEPS.REFINEMENT)
            },
            onError: (err) => {
                setError(err?.message || 'Failed to modify configuration')
            },
        })
    }

    // Handle accept
    const handleAccept = () => {
        if (!datasetName?.trim()) return
        // Block until the object list resolves: finishing early would silently
        // send the preset's placeholder instead of the real object.
        if (objectListLoading) return
        if (requireObjectSelection && !selectedObjectName) return
        accept({
            sessionId,
            onSuccess: (response) => {
                setConversation(response)
                onComplete({
                    objectName: response.objectName,
                    objectLabel: response.objectLabel,
                    fieldNames: response.fieldNames,
                    fieldLabels: response.fieldLabels,
                    filters: response.filters,
                    dateRange: requireDateRange
                        ? { ...(response.dateRange || {}), start: startDate }
                        : response.dateRange,
                    // For connectors with a real object list (e.g. GA4), the
                    // picked object replaces the preset's placeholder.
                    object: requireObjectSelection
                        ? objectList.find((o) => o.name === selectedObjectName)
                        : undefined,
                    datasetName: datasetName.trim(),
                })
            },
            onError: (err) => {
                setError(err?.message || 'Failed to accept configuration')
            },
        })
    }

    // Handle cancel - go back to setup choice
    const handleCancel = () => {
        if (sessionId) {
            cancel({
                sessionId,
                onSuccess: () => onCancel(),
                onError: () => onCancel(),
            })
        } else {
            onCancel()
        }
    }

    // Handle start over
    const handleStartOver = () => {
        if (sessionId) {
            cancel({
                sessionId,
                onSuccess: () => {
                    setSessionId(null)
                    setConversation(null)
                    setError(null)
                    setCurrentStep(STEPS.PRESET_SELECTION)
                    setStepHistory([])
                    setSelectedPreset(null)
                    setCustomText('')
                    setModifyText('')
                    setDatasetName('')
                    setStartDate(null)
                    startConversation({
                        connectorType,
                        connectionKey,
                        workspaceID,
                        onSuccess: (response) => {
                            setSessionId(response.sessionId)
                            setConversation(response)
                        },
                        onError: (err) => {
                            setError(err?.message || 'Failed to restart conversation')
                        },
                    })
                },
                onError: () => {
                    setError('Failed to restart conversation')
                },
            })
        }
    }

    const handleCloseDialog = (e, reason) => {
        if (reason && reason === 'backdropClick') return
        onClose?.()
    }

    // Handle Next button for preset selection
    const handlePresetNext = () => {
        if (!selectedPreset) return
        if (selectedPreset === 'something_else') {
            goToStep(STEPS.CUSTOM_INTENT)
        } else {
            handleSelectPreset(selectedPreset)
        }
    }

    // Render content based on current step
    const renderContent = () => {
        // Loading state while starting conversation
        if (startingConversation || !conversation) {
            return (
                <div className={styles.loadingContainer}>
                    <ScoopSpinner size={40} />
                    <span className={styles.loadingText}>Starting AI-guided configuration...</span>
                </div>
            )
        }

        // Error state
        if (error) {
            return (
                <div className={styles.errorContainer}>
                    <span className={styles.errorText}>{error}</span>
                    <div className={styles.errorActions}>
                        <ScoopButton variant="outline" onClick={handleStartOver}>
                            Try again
                        </ScoopButton>
                        <ScoopButton variant="text" onClick={handleCancel}>
                            Cancel
                        </ScoopButton>
                    </div>
                </div>
            )
        }

        // Accepted state - creating dataset
        if (conversation?.phase === 'ACCEPTED') {
            return (
                <div className={styles.loadingContainer}>
                    <ScoopSpinner size={40} />
                    <span className={styles.loadingText}>Creating your dataset...</span>
                </div>
            )
        }

        switch (currentStep) {
            case STEPS.PRESET_SELECTION:
                return (
                    <PresetSelection
                        presets={conversation.availablePresets || []}
                        selectedPreset={selectedPreset}
                        onSelectionChange={setSelectedPreset}
                        loading={selectingPreset}
                    />
                )

            case STEPS.CUSTOM_INTENT:
                return (
                    <CustomIntent
                        value={customText}
                        onChange={setCustomText}
                        placeholder={conversation.somethingElsePrompt}
                        loading={selectingPreset}
                    />
                )

            case STEPS.REFINEMENT:
                return (
                    <RefinementChat
                        conversation={conversation}
                        datasetName={datasetName}
                        onDatasetNameChange={setDatasetName}
                        startDate={startDate}
                        onStartDateChange={setStartDate}
                        requireDateRange={requireDateRange}
                        requireObjectSelection={requireObjectSelection}
                        objectList={objectList}
                        objectSelectionLabel={objectSelectionLabel}
                        selectedObjectName={selectedObjectName}
                        onObjectChange={setSelectedObjectName}
                        loading={accepting}
                    />
                )

            case STEPS.MODIFY_INPUT:
                return (
                    <ModifyInput
                        value={modifyText}
                        onChange={setModifyText}
                        loading={modifyingConfig}
                    />
                )

            default:
                return null
        }
    }

    // Render dialog actions based on current step
    const renderDialogActions = () => {
        const isLoading = startingConversation || selectingPreset || modifyingConfig || accepting

        // Don't show actions while loading or in error/accepted state
        if (isLoading || error || !conversation || conversation?.phase === 'ACCEPTED') {
            return (
                <ScoopButton variant="text" onClick={handleCancel}>
                    Cancel
                </ScoopButton>
            )
        }

        const isConvergenceWarning = conversation?.phase === 'CONVERGENCE_WARNING'

        switch (currentStep) {
            case STEPS.PRESET_SELECTION:
                return (
                    <>
                        <ScoopButton variant="text" onClick={goBack}>
                            Back
                        </ScoopButton>
                        <ScoopButton onClick={handlePresetNext} disabled={!selectedPreset}>
                            Next
                        </ScoopButton>
                    </>
                )

            case STEPS.CUSTOM_INTENT:
                return (
                    <>
                        <ScoopButton variant="text" onClick={goBack}>
                            Back
                        </ScoopButton>
                        <ScoopButton
                            onClick={handleCustomIntentSubmit}
                            disabled={!customText.trim()}
                        >
                            Next
                        </ScoopButton>
                    </>
                )

            case STEPS.REFINEMENT:
                return (
                    <>
                        <ScoopButton variant="text" onClick={goBack}>
                            Back
                        </ScoopButton>
                        {isConvergenceWarning && (
                            <>
                                <ScoopButton variant="text" onClick={onFallbackToManual}>
                                    Configure manually
                                </ScoopButton>
                                <ScoopButton variant="text" onClick={handleStartOver}>
                                    Start over
                                </ScoopButton>
                            </>
                        )}
                        <ScoopButton
                            variant="outline"
                            onClick={() => goToStep(STEPS.MODIFY_INPUT)}
                            disabled={conversation?.modificationRound >= 5}
                        >
                            Change something
                        </ScoopButton>
                        <ScoopButton
                            onClick={handleAccept}
                            disabled={
                                !datasetName?.trim() ||
                                objectListLoading ||
                                (requireObjectSelection && !selectedObjectName)
                            }
                        >
                            Create dataset
                        </ScoopButton>
                    </>
                )

            case STEPS.MODIFY_INPUT:
                return (
                    <>
                        <ScoopButton variant="text" onClick={goBack}>
                            Back
                        </ScoopButton>
                        <ScoopButton onClick={handleModifySubmit} disabled={!modifyText.trim()}>
                            Apply changes
                        </ScoopButton>
                    </>
                )

            default:
                return (
                    <ScoopButton variant="text" onClick={goBack}>
                        Back
                    </ScoopButton>
                )
        }
    }

    return (
        <ScoopDialog
            open={open}
            onClose={handleCloseDialog}
            title={`Dataset from ${connectorName || connectorType}`}
            actions={renderDialogActions()}
            maxWidth="lg"
        >
            <div className={styles.container}>{renderContent()}</div>
        </ScoopDialog>
    )
}
